/**
 * PbPhaseBar — Phasen-Stepper als volle Breite ganz oben im PB-Profil.
 *
 * Vorher lag der Phasen-Block in der linken Sidebar (PbSidebar). Mit dem
 * Profil-Redesign rückt er an erste Stelle direkt unter die Navbar, über das
 * Zwei-Spalten-Grid — damit der Prozessfortschritt sofort sichtbar ist.
 *
 * Kapselt den eigenen Phasen-State (optimistisches Advance via v2Users.setPhase),
 * exakt wie zuvor in der Sidebar.
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Button } from '../../../components/ui';
import { getThemeColors } from '../../../lib/theme';
import { type V1User, type PbPhase, v2Users, v2Phases, phaseRuleErrors } from '../../../lib/v1';
import { toast } from '../../../lib/toast';
import { PbPhaseStepper, PhaseBadge, PB_PHASE_COUNT } from '../../../components/PbPhaseStepper';
import { PhaseRulesBlock } from '../../../components/care/PhaseRulesBlock';

interface PbPhaseBarProps {
  user: V1User | null;
  dark: boolean;
  /** DB phases — when provided, passed through to PbPhaseStepper for dynamic rendering. */
  phases?: PbPhase[];
  /** Called after a phase update so the parent can refresh its cache. */
  onPhaseChange?: (phase: number, phaseLabel: string | null) => void;
}

export function PbPhaseBar({ user, dark, phases, onPhaseChange }: PbPhaseBarProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();

  const [phase, setPhase] = useState<number>(() =>
    typeof user?.phase === 'number' ? user.phase : (user?.phase ? Number(user.phase) : 1),
  );
  const [phaseLabel, setPhaseLabel] = useState<string | null>(() =>
    typeof user?.phase_label === 'string' ? user.phase_label : null,
  );
  const [advancing, setAdvancing] = useState(false);
  const [phaseError, setPhaseError] = useState<string | null>(null);

  // DB-Phasen: wenn der Parent keine `phases` durchreicht, selbst laden, damit
  // im PB-Profil Titel/Farbe/Anzahl aus den Einstellungen (pb_phases) kommen.
  // Fällt bei Fehler auf die hardcodierten Defaults im Stepper zurück.
  const [effectivePhases, setEffectivePhases] = useState<PbPhase[] | undefined>(phases);
  useEffect(() => {
    if (phases && phases.length > 0) { setEffectivePhases(phases); return; }
    let alive = true;
    v2Phases.list()
      .then(list => { if (alive && list.length > 0) setEffectivePhases(list); })
      .catch(() => { /* Stepper nutzt hardcodierte Defaults */ });
    return () => { alive = false; };
  }, [phases]);

  // Nur für bestehende PB (mit id) anzeigen — neue Profile haben keine Phase.
  if (user?.id == null) return null;

  const maxPhaseId = effectivePhases && effectivePhases.length > 0
    ? Math.max(...effectivePhases.map(p => p.id))
    : PB_PHASE_COUNT;

  const handleAdvance = async () => {
    if (!user?.id || advancing || phase >= maxPhaseId) return;
    setAdvancing(true);
    setPhaseError(null);
    const optimistic = Math.min(phase + 1, maxPhaseId);
    setPhase(optimistic);
    try {
      const result = await v2Users.setPhase(user.id, { action: 'advance' });
      setPhase(result.phase);
      setPhaseLabel(result.phase_label);
      onPhaseChange?.(result.phase, result.phase_label ?? null);
    } catch (e) {
      setPhase(phase);
      const unmet = phaseRuleErrors(e);
      if (unmet) {
        toast(unmet, 'warning', tr('pflege.phase.advance_blocked_title'));
      } else {
        setPhaseError(e instanceof Error ? e.message : tr('pflege.phase.advance_error'));
      }
    } finally {
      setAdvancing(false);
    }
  };

  /**
   * Finale Stage erreicht?
   *
   * Maßgeblich ist die REIHENFOLGE (phase.sort), nicht die ID — eine später
   * angelegte Stage hat eine hohe ID, kann aber weit vorne stehen (gleiche
   * Falle wie im PhaseRulesBlock, Bugreport 18.09.2026). Ohne geladene
   * DB-Phasen bleibt die ID-Näherung als Fallback.
   */
  const currentSort = effectivePhases?.find(p => p.id === phase)?.sort;
  const atMax = effectivePhases && effectivePhases.length > 0 && currentSort !== undefined
    ? !effectivePhases.some(p => p.sort > currentSort)
    : phase >= maxPhaseId;

  // Die letzte Stage allein macht noch keinen Kunden (dazu braucht es auch
  // eine bestätigte Care) — der Balken bleibt deshalb sichtbar, "Weiter" ist
  // nur deaktiviert. Ausgeblendet wird er erst in PbDetailPage (isKunde).

  return (
    // marginBottom gehört zur Karte, nicht zum Wrapper in PbDetailPage: so
    // verschwindet der Abstand mit der Karte, wenn sie ausgeblendet wird.
    <Card dark={dark} style={{ padding: '14px 18px', marginBottom: 16 }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 16, flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: t.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {tr('pflege.phase.header')}
          </span>
          <PhaseBadge phase={phase} phaseLabel={phaseLabel} dark={dark} />
        </div>
        {/* Stepper ohne eigenen Button (K4: Weiter-Button steht separat rechts) */}
        {/* flex-basis statt minWidth: auf dem Handy ist die Karte schmaler
            als 240px — der Stepper lief sonst rechts aus der Karte. */}
        <div style={{ flex: '1 1 240px', minWidth: 0 }}>
          <PbPhaseStepper
            phase={phase}
            phases={effectivePhases}
            showAdvance={false}
            dark={dark}
          />
        </div>
        {/* K4: „Weiter"-Button rechts im Phasenbereich */}
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          <Button
            variant="primary"
            size="sm"
            dark={dark}
            disabled={atMax || advancing}
            onClick={handleAdvance}
          >
            {advancing
              ? tr('pflege.phase.advancing')
              : atMax
                ? tr('pflege.phase.completed')
                : tr('pflege.phase.advance')}
          </Button>
        </div>
      </div>
      {phaseError && (
        <div style={{ marginTop: 8, fontSize: 12, color: '#b42318' }}>{phaseError}</div>
      )}
      {/* Bedingungen der nächsten Stage — dieselben ausfüllbaren Felder wie im
          Anfragen-Drawer (ersetzt die frühere "Phasen-Daten"-Card). Der lokale
          phase-State fließt ein, damit nach "Weiter" die Folge-Stage erscheint. */}
      <div style={{ marginTop: 12, borderTop: `1px solid ${t.borderSoft}`, paddingTop: 12 }}>
        <PhaseRulesBlock dark={dark} raw={{ ...user, phase } as V1User} />
      </div>
    </Card>
  );
}
